import * as React from 'react';
import { Loader2, UploadCloudIcon } from 'lucide-react';
import { cn } from '../lib/utils';

export interface FileDropzoneProps {
  /** Called once per chosen or dropped file. */
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  loading?: boolean;
  title?: React.ReactNode;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  /** Compact single-line variant, for a dropzone inside a table row or card header. */
  compact?: boolean;
}

/**
 * Click-or-drop file picker — the replacement for antd's `<Upload.Dragger>`.
 *
 * It only *selects* files. What happens next (presign, PUT to S3, confirm) is
 * the caller's job, because every upload in this product goes browser → S3
 * through a presigned URL and the steps differ per resource.
 */
export function FileDropzone({
  onFiles,
  accept,
  multiple = false,
  disabled = false,
  loading = false,
  title = 'Click or drag a file here',
  hint,
  icon,
  className,
  compact = false,
}: FileDropzoneProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = React.useState(false);
  const inactive = disabled || loading;

  const pick = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const files = Array.from(list);
    onFiles(multiple ? files : files.slice(0, 1));
  };

  return (
    <div
      role="button"
      tabIndex={inactive ? -1 : 0}
      aria-disabled={inactive}
      onClick={() => !inactive && inputRef.current?.click()}
      onKeyDown={(event) => {
        if (inactive) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        if (!inactive) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        if (!inactive) pick(event.dataTransfer.files);
      }}
      className={cn(
        'flex cursor-pointer items-center justify-center rounded-lg border border-dashed border-input bg-muted/30 text-center transition-colors outline-none',
        'hover:border-ring hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50',
        dragging && 'border-ring bg-muted/70',
        inactive && 'pointer-events-none opacity-60',
        compact ? 'gap-2 px-3 py-2.5' : 'flex-col gap-2 px-6 py-8',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={(event) => {
          pick(event.target.files);
          // Reset so choosing the same file twice still fires a change.
          event.target.value = '';
        }}
      />
      <span className="text-muted-foreground">
        {loading ? (
          <Loader2 className={cn('animate-spin', compact ? 'size-4' : 'size-6')} />
        ) : (
          (icon ?? <UploadCloudIcon className={compact ? 'size-4' : 'size-6'} />)
        )}
      </span>
      <div className={cn('grid', compact ? 'text-start' : 'gap-1')}>
        <span className="text-sm font-medium">{title}</span>
        {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
      </div>
    </div>
  );
}
